import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckIcon } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Button } from '@/components/ui-kit/button';
import { useListOrganizations } from '@/lib/api/hooks/use-multi-orgs';
import { excludeDefaultOrganizations, isOrganizationEnabled } from '@/lib/auth/org-access';
import { useAssignToOrganization } from '@/hooks/use-assign-to-organization';
import { useSignoutMutation } from '@/modules/auth/hooks/use-auth';
import { useAuthStore } from '@/state/store/auth';
import {
  clearOrgSelectionRequired,
  clearPendingSignupOrganizationId,
} from '@/lib/auth/org-selection';
import { cn } from '@/lib/utils';

type ForceOrganizationDialogProps = {
  open: boolean;
};

/**
 * Blocking dialog for existing users who only have Default org access.
 * They must pick a real organization before continuing — or log out.
 */
export const ForceOrganizationDialog = ({ open }: ForceOrganizationDialogProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const { mutateAsync: signout, isPending: isSigningOut } = useSignoutMutation();
  const { assignAndSwitch, isBusy } = useAssignToOrganization({ enabled: open });
  const {
    data: orgsData,
    isLoading,
    isError,
    refetch,
  } = useListOrganizations({ page: 0, pageSize: 10 }, { enabled: open });

  const availableOrganizations = useMemo(
    () =>
      excludeDefaultOrganizations(
        (orgsData?.organizations ?? []).filter(isOrganizationEnabled)
      ),
    [orgsData?.organizations]
  );

  const [pickedId, setPickedId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) setPickedId(null);
  }, [open]);

  const isWorking = isBusy || isSigningOut;
  const canContinue = Boolean(pickedId) && !isWorking && !isLoading && !isError;

  const helperCopy = useMemo(() => {
    if (isLoading) return t('LOADING') || 'Loading organizations…';
    if (isError) return 'Could not load organizations. Try again, or log out to use a different account.';
    if (availableOrganizations.length === 0) {
      return 'No organizations are available. Contact an administrator, or log out.';
    }
    return 'Your account is on the Default organization. Select an organization to continue.';
  }, [availableOrganizations.length, isError, isLoading, t]);

  const handleLogout = async () => {
    try {
      await signout();
    } catch {
      // Still clear local session so the user is not stuck behind the dialog.
    } finally {
      clearOrgSelectionRequired();
      clearPendingSignupOrganizationId();
      logout();
      navigate('/login', { replace: true });
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        hideClose
        className="max-w-md gap-5"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Select organization</DialogTitle>
          <DialogDescription>{helperCopy}</DialogDescription>
        </DialogHeader>

        <div className="flex max-h-[280px] flex-col gap-1 overflow-y-auto rounded-xl border border-neutral-200 p-2">
          {isLoading ? (
            <p className="px-3 py-8 text-center text-sm text-neutral-500">Loading…</p>
          ) : isError ? (
            <div className="flex flex-col items-center gap-3 px-3 py-6">
              <p className="text-center text-sm text-destructive">Failed to load organizations.</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isWorking}
                onClick={() => void refetch()}
              >
                Try again
              </Button>
            </div>
          ) : availableOrganizations.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-neutral-500">No organizations found.</p>
          ) : (
            availableOrganizations.map((org) => {
              const isSelected = org.itemId === pickedId;
              return (
                <button
                  key={org.itemId}
                  type="button"
                  disabled={isWorking}
                  onClick={() => setPickedId(org.itemId)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm transition-colors',
                    isSelected
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-neutral-900 hover:bg-neutral-50'
                  )}
                >
                  <span className="min-w-0 flex-1 truncate font-medium">{org.name}</span>
                  <span className="flex size-5 shrink-0 items-center justify-center">
                    {isSelected ? <CheckIcon className="size-4 text-primary-600" aria-hidden /> : null}
                  </span>
                </button>
              );
            })
          )}
        </div>

        <div className="flex w-full flex-col gap-2">
          <Button
            type="button"
            className="w-full"
            disabled={!canContinue}
            loading={isBusy}
            onClick={() => {
              if (!pickedId) return;
              void assignAndSwitch(pickedId);
            }}
          >
            Continue
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled={isWorking}
            loading={isSigningOut}
            onClick={() => void handleLogout()}
          >
            {t('LOG_OUT') || 'Log out'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
